import DateUtils from 'core/utils/date'
import { ISport } from 'core/api/rest/queries/select-sport'
import IDataSource from 'core/models/data-source'
import { ITeam } from 'core/api/rest/queries/select-teams'
import { parseWeek } from './weeks'
import { IAggregateHeader } from './aggregates'
import { pad } from './number'
import { Sport } from 'core/models/api/sports-piloting/sports-piloting'
import { ISportsGroupComposition } from 'core/models/api/sports-group/sports-group-piloting'
import i18next from 'i18next'

export type IAbstractHeader = IIndicatorHeader | IColumnHeader | IAggregateHeader

export interface IHeaders<T = IAbstractHeader> {
  [key: string]: T
}

export interface IHeader {
  value: string | number
  index: number
  content?: IHeaderContent
  id?: string
  /** used to define if header column is fixed in table on scroll **/
  isFixed?: boolean
  /** used to define header column can be extend/collapse **/
  isToggleable?: boolean
  isAggregate?: boolean
  isHidden?: boolean
}

export interface IHeaderContent {
  title: string
  subtitle?: string
}

export interface IIndicatorHeader extends IHeader {
  isFixed: boolean
  value: string
}

export interface IColumnHeader extends IHeader {
  content: IHeaderContent
}

export interface IHeaderIndex {
  id?: string | number
  sport_id?: number
  index: number
}

/**
 * Used to get account's column information
 * @headerIndex return index position of indicator column
 * Allow components which build table to apply style/effects if indicator column has different positions into the table
 */
export const ACCOUNT_HEADER_ID = 'ACCOUNTS'

export const SORT_STATUS = {
  ASC: 'ASC',
  DESC: 'DESC',
  NONE: 'NONE'
}

export const getFixedColumnsIndexes = (headers: IHeaders): number[] =>
  Object.keys(headers).reduce<number[]>((fixedColumns, headerId) => {
    const { isFixed, index } = headers[headerId]
    if (isFixed && index !== undefined) {
      return [...new Set([...fixedColumns, index])]
    }
    return fixedColumns
  }, [])

/**
 * Used to build and get indicator header
 * @return indicator header
 */
export const getIndicatorHeader = (index = 0): IHeaders<IIndicatorHeader> => ({
  [ACCOUNT_HEADER_ID]: {
    isFixed: true,
    value: i18next.t('pilotable.indicators'),
    index
  }
})

export const getSortedSportHeaders = (
  headers: IHeaders,
  sportsIndexes: IHeaderIndex[],
  offsetIndex = 0,
  idAttribute: 'sport_id' | 'id' = 'id'
): IHeaders => {
  if (!sportsIndexes) {
    return Object.keys(headers).reduce<IHeaders>(
      (headers, headerId, index) => ({
        ...headers,
        [headerId]: { ...headers?.[headerId], index: index + offsetIndex }
      }),
      headers
    )
  }
  return sportsIndexes?.reduce<IHeaders>(
    (headers, { [idAttribute]: id, index }) => ({ ...headers, [id]: { ...headers[id], index: index + offsetIndex } }),
    headers
  )
}

/**
 * @param sports
 * @param sportsIndexes
 * @param offsetIndex // used when there are columns headersdisplayed before aggregates headers columns
 */
export const getStoreSportHeaders = (
  sports: Sport[],
  sportsIndexes: ISportsGroupComposition[] | null,
  offsetIndex = 0
): IHeaders => {
  const sportsHeaders = sports.reduce<IHeaders<IColumnHeader>>((headers, sport, index) => {
    return {
      ...headers,
      [sport.id]: {
        content: {
          title: sport.store_label
        },
        value: `${sport.department_id} - ${sport.label}`,
        index: index + offsetIndex
      }
    }
  }, {})
  return getSortedSportHeaders(sportsHeaders, sportsIndexes, offsetIndex, 'sport_id')
}

export const getDataHeaders = (teams: IDataSource[], headerIndexes: IHeaderIndex[], offsetIndex: number): IHeaders => {
  const headers = teams?.reduce<IHeaders>((headers, team, index) => {
    return {
      ...headers,
      [Number(team.id)]: {
        value: team.label,
        index: index + offsetIndex
      }
    }
  }, {})

  return getSortedSportHeaders(headers, headerIndexes, offsetIndex)
}

/**
 * @param sports
 * @param sportsIndexes
 * @param offsetIndex // used when there are columns headersdisplayed before aggregates headers columns
 */
export const getSportWithDepartmentIdHeaders = (
  sports: Sport[],
  sportsIndexes: IHeaderIndex[],
  offsetIndex = 0
): IHeaders => {
  const sportsHeaders = sports.reduce<IHeaders>((headers, sport, index) => {
    return {
      ...headers,
      [sport.id]: {
        content: {
          title: sport.department_id
        },
        value: sport.label,
        index: index + offsetIndex
      }
    }
  }, {})

  return getSortedSportHeaders(sportsHeaders, sportsIndexes, offsetIndex, 'sport_id')
}

/**
 * @param teams
 * @param sportsIndexes
 * @param offsetIndex // used when there are columns headersdisplayed before aggregates headers columns
 */
export const getTeamsWithDepartmentIdHeaders = (
  teams: ITeam[],
  sportsIndexes: IHeaderIndex[],
  offsetIndex = 0
): IHeaders => {
  const teamsHeaders = teams.reduce<IHeaders>((headers, team, index) => {
    return {
      ...headers,
      [Number(team.id)]: {
        content: {
          title: team.id
        },
        value: team.label,
        index: index + offsetIndex
      }
    }
  }, {})

  return getSortedSportHeaders(teamsHeaders, sportsIndexes, offsetIndex, 'sport_id')
}

/**
 * Used to build and get week headers
 * @param weekList
 * @param offsetIndex // used when there are columns headersdisplayed before aggregates headers columns
 * @return week headers
 * @example getWeekHeaders(["2019W01, ..., 2019W52"]) =>
 * {"2019W01": { year: "2019", week: "01" }, ..., "2019W52": { year: "2019", week: "52" }}
 */
export const getWeekHeaders = (weekList: string[], offsetIndex = 0): IHeaders<IColumnHeader> =>
  weekList.reduce((headers, header, index) => {
    const { year, week } = parseWeek(header)
    return {
      ...headers,
      [header]: {
        content: {
          title: year
        },
        value: week,
        index: index + offsetIndex
      }
    }
  }, {})

export enum Period {
  HISTORY = 'HISTORY',
  PILOTING = 'PILOTING',
  TRAJECTORY = 'TRAJECTORY',
  PILOTING_N_1 = 'PILOTING_N_1'
}

/**
 * Transform a period to a date
 * @param period
 */
export const periodToDate = (period: Period): string => {
  const date = DateUtils.getCurrentDate()
  const month = pad(DateUtils.getMonthNumeric(date))

  switch (period) {
    case Period.HISTORY: {
      const prevYear = DateUtils.getPreviousYearNumber(date)
      return `${prevYear}-${month}`
    }
    case Period.PILOTING:
    case Period.TRAJECTORY: {
      const year = date.getFullYear()
      return `${year}-${month}`
    }
    case Period.PILOTING_N_1: {
      const nextYear = DateUtils.getNextYearNumber(date)
      return `${nextYear}-${month}`
    }
    default:
      return null
  }
}

/**
 * It's possible that the backend does not return each team sports.
 * So we add missing sports (with only their id) to the sport list.
 */
export const getFullSportListFromTeam = (sports: Sport[], team: ITeam): Sport[] => {
  if (sports == null) {
    throw new Error('sports must not be null or undefined')
  }

  const sportsIds = sports?.map(sport => sport.id)
  const missingSportsIds = team?.composition?.filter(sport => !sportsIds.includes(sport)) || []
  const missingSports = missingSportsIds.map<ISport>(sportId => ({
    id: sportId,
    department_id: null,
    label: sportId.toString(),
    store_id: null,
    store_label: null
  }))
  return [...sports, ...missingSports]
}
